import React, { useContext, useMemo, useState } from 'react'

import ProfileContext from '@/components/Context/ProfileContext'
import RecommendCard, { DEFAULT_PREVIEW_COUNT } from '@/components/Recommend/RecommendCard'
import RecommendSection from '@/components/Recommend/RecommendSection'
import useEnrichProducts from '@/lib/hook/useEnrichProducts'
import { useInternalRecommend } from '@/lib/hook/useInternalRecommend'
import { withOwnReason, withoutProducts } from '@/lib/recommendFilters'

const PLACEMENT = 'home'

/**
 * Recommandations de l'accueil. Une pharmacie dans le test des nouveaux blocs, si elle a un
 * historique, voit quatre blocs tirés de ses achats ; toutes les autres gardent RecommendSection.
 */
const HomeRecommend: React.FC = () => {
  const { profile, loadingProfile } = useContext(ProfileContext)
  const enabled = !loadingProfile && !!profile?.recommendBlocksV2
  const [expanded, setExpanded] = useState<string | null>(null)

  const reorder = useInternalRecommend({ model: 'reorder', maxRecommendations: 8, enabled })
  const personalized = useInternalRecommend({ model: 'personalized', maxRecommendations: 8, enabled })
  const discovery = useInternalRecommend({ model: 'discovery', maxRecommendations: 4, enabled })
  const season = useInternalRecommend({ model: 'season', maxRecommendations: 8, enabled })

  const loading = reorder.loading || personalized.loading || discovery.loading || season.loading

  const lightProducts = useMemo(
    () => [...reorder.products, ...personalized.products, ...discovery.products, ...season.products],
    [reorder.products, personalized.products, discovery.products, season.products]
  )
  const { products: enrichedProducts } = useEnrichProducts(lightProducts, enabled && !loading)

  const cards = useMemo(() => {
    const enrichedBySlug = new Map(enrichedProducts.map(product => [product.seo_name, product]))
    const enrich = (products: any[]) =>
      products.map(p => withOwnReason(enrichedBySlug.get(p.seo_name) ?? p, p))

    const definitions = [
      {
        theme: 'reorder',
        title: t({ id: 'web_reco_reorder_title' }),
        subtitle: t({ id: 'web_reco_reorder_subtitle' }),
        products: reorder.products,
      },
      {
        theme: 'personalized',
        title: t({ id: 'web_reco_personalized_title' }),
        subtitle: t({ id: 'web_reco_personalized_subtitle' }),
        products: personalized.products,
      },
      {
        theme: 'discovery',
        title: t({ id: 'web_reco_discovery_title' }),
        subtitle: t({ id: 'web_reco_discovery_subtitle' }),
        products: discovery.products,
      },
      {
        theme: 'season',
        title: t({ id: 'web_reco_season_title' }),
        subtitle: t({ id: 'web_reco_season_subtitle' }, { categories: season.seasonCategories.join(', ') }),
        products: season.products,
      },
    ]

    // Un même produit n'apparaît pas dans deux blocs de la page.
    const shown: string[] = []
    return definitions.flatMap(definition => {
      const products = withoutProducts(enrich(definition.products), shown)
      if (products.length === 0) return []
      shown.push(...products.slice(0, DEFAULT_PREVIEW_COUNT).map(p => String(p.product_id)))
      return [{ ...definition, products }]
    })
  }, [enrichedProducts, reorder.products, personalized.products, discovery.products, season.products, season.seasonCategories])

  if (loadingProfile || (enabled && loading)) return null
  // Sans historique, la liste est vide : les blocs habituels restent les meilleurs.
  if (!enabled || personalized.products.length === 0) return <RecommendSection />

  return (
    <div className={`grid gap-4 my-4 ${expanded ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
      {cards
        .filter(card => !expanded || card.theme === expanded)
        .map(card => (
          <RecommendCard
            key={card.theme}
            title={card.title}
            subtitle={card.subtitle}
            products={card.products}
            theme={card.theme}
            defaultListView
            expanded={expanded === card.theme}
            onToggle={() => setExpanded(expanded === card.theme ? null : card.theme)}
            placement={`${PLACEMENT}:${card.theme}`}
          />
        ))}
    </div>
  )
}

export default HomeRecommend
